/**
 * StreetDiceRoom — the actual circle. 6 seats around a felt/asphalt ring,
 * shooter in the middle, dice animation, announce/cover/roll controls.
 * Polls /api/street-dice/rooms/{id} every 2 s (MVP transport). Will migrate
 * to WebSockets in a later phase.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Coins, Dice5, DoorOpen, HelpCircle, Loader2,
    Palette, Users, LogIn, Bot, Trophy, Skull, Sparkles, Share2,
    Eye, Flame, Target, Plus, X, Check, Volume2, VolumeX,
    Crown, Banknote, RotateCcw, Eraser,
} from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { getSessionTheme, shuffleTheme, ThemeOverlay } from './StreetDiceThemes';
import { HouseRulesDialog } from './StreetDiceLobby';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PIP_LAYOUTS = {
    1: [[1, 1]],
    2: [[0, 0], [2, 2]],
    3: [[0, 0], [1, 1], [2, 2]],
    4: [[0, 0], [2, 0], [0, 2], [2, 2]],
    5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
    6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

// Position 6 seats evenly around the circle, shooter at top
const seatPosition = (idx, count, radius = 42) => {
    const angle = (idx / count) * 2 * Math.PI - Math.PI / 2;
    return {
        left: `${50 + Math.cos(angle) * radius}%`,
        top: `${50 + Math.sin(angle) * radius}%`,
    };
};

const Die = ({ value, rolling, theme }) => (
    <motion.div
        className="relative w-14 h-14 md:w-20 md:h-20 rounded-xl shadow-lg border-2"
        style={{ background: theme.dieBg, borderColor: theme.dieBorder }}
        animate={rolling ? { rotate: [0, 180, 540, 720], y: [0, -16, -8, 0] } : { rotate: 0, y: 0 }}
        transition={rolling ? { duration: 0.9, ease: 'easeOut' } : { duration: 0 }}
        data-testid={`street-die-${value ?? '?'}`}
    >
        {value != null && (
            <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-0.5">
                {[...Array(9)].map((_, i) => {
                    const col = i % 3;
                    const row = Math.floor(i / 3);
                    const pips = PIP_LAYOUTS[value] || [];
                    const on = pips.some(([c, r]) => c === col && r === row);
                    return (
                        <div
                            key={i}
                            className="rounded-full"
                            style={{ background: on ? theme.diePip : 'transparent' }}
                        />
                    );
                })}
            </div>
        )}
    </motion.div>
);

const SeatChip = ({ seat, isShooter, isFader, isMe, theme, onFadePreview }) => (
    <div
        className="rounded-xl px-2.5 py-2 min-w-[110px] text-center relative"
        style={{
            background: seat.is_bot ? 'rgba(255,255,255,0.06)' : theme.seatChip,
            border: `1px solid ${isShooter ? theme.accent : isFader ? '#22c55e' : theme.seatBorder}`,
            boxShadow: isShooter ? `0 0 20px ${theme.glow}` : isFader ? '0 0 14px rgba(34,197,94,0.5)' : 'none',
        }}
        data-testid={`street-dice-seat-${seat.user_id}`}
    >
        {isShooter && (
            <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded text-black"
                style={{ background: theme.accent }}
            >
                SHOOTER
            </div>
        )}
        {!isShooter && isFader && (
            <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded text-black"
                style={{ background: '#22c55e' }}
                data-testid={`street-dice-fader-badge-${seat.user_id}`}
            >
                FADER
            </div>
        )}
        <div className="flex items-center justify-center gap-1 text-white">
            {seat.is_bot ? <Bot className="w-3 h-3 text-white/60" /> : null}
            <span
                className="text-sm font-bold truncate max-w-[80px]"
                style={{ fontFamily: theme.headingFont, letterSpacing: '0.04em' }}
            >
                {seat.name}
            </span>
        </div>
        {seat.personality && (
            <div className="text-[8px] font-mono text-white/40 uppercase tracking-widest">
                {seat.personality}
            </div>
        )}
        <div className="text-[11px] font-mono mt-1" style={{ color: theme.potColor }}>
            {fmt$(seat.room_balance)}
        </div>
        {isMe && (
            <div
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[8px] font-black px-1 py-0.5 rounded text-black"
                style={{ background: theme.accentSoft }}
            >
                YOU
            </div>
        )}
    </div>
);

export default function StreetDiceRoom() {
    const { id: roomId } = useParams();
    const [searchParams] = useSearchParams();
    const inviter = searchParams.get('invite');
    const urlJoinCode = searchParams.get('code');
    const urlHostId = searchParams.get('host');
    const nav = useNavigate();
    const { user, refreshUser } = useAuth();

    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [theme, setTheme] = useState(() => getSessionTheme());
    const [busy, setBusy] = useState(false);
    const [rolling, setRolling] = useState(false);
    const [joinOpen, setJoinOpen] = useState(false);
    const [buyIn, setBuyIn] = useState('');
    const [announceAmt, setAnnounceAmt] = useState('');
    const [coverAmt, setCoverAmt] = useState('');
    const [lastAnnouncedAmt, setLastAnnouncedAmt] = useState(() => {
        try { return localStorage.getItem('street-dice-last-wager') || ''; }
        catch { return ''; }
    });
    const [rulesOpen, setRulesOpen] = useState(false);
    const [celebration, setCelebration] = useState(null); // { title, sub, tone }
    const [wsLive, setWsLive] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [reactions, setReactions] = useState([]); // floating emote bursts over the pit
    const reactionSentAtRef = useRef(0);
    const [sideBetPostOpen, setSideBetPostOpen] = useState(false); // poster/challenge dialog (opened from SIDE BETS btn)
    const [chatFlash, setChatFlash] = useState(null); // { id, name, text, human } — full-screen quick flash
    const [chatDraft, setChatDraft] = useState('');
    const [typingUsers, setTypingUsers] = useState({}); // { user_id: { name, expiresAt } }
    const [mentionQuery, setMentionQuery] = useState(null); // { start, prefix } | null
    const chatInputRef = useRef(null);
    const typingSentAtRef = useRef(0);
    const typingClearTimerRef = useRef(null);
    const [winFlash, setWinFlash] = useState(null);
    const [houseTakeFlash, setHouseTakeFlash] = useState(null); // { id, amount, source }
    const [forceFadeFlash, setForceFadeFlash] = useState(null); // { id, name, amount, reason, self }
    const [topUpOpen, setTopUpOpen] = useState(false);
    const [topUpBusy, setTopUpBusy] = useState(false);
    const [topUpClaimed, setTopUpClaimed] = useState('');
    const [topUpPayPalOpened, setTopUpPayPalOpened] = useState(false);
    const [topUpReturnPulse, setTopUpReturnPulse] = useState(false);
    const topUpFileRef = useRef(null);
    const lastRollIdRef = useRef(null);
    const inviteToastShownRef = useRef(false);
    const lowBalanceShownRef = useRef(false);
    const lowBalanceToastIdRef = useRef(null);
    const wsRef = useRef(null);
    const wsBackoffRef = useRef(1000);
    const wsClosedByUsRef = useRef(false);
    const audioCtxRef = useRef(null);
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem('sd_muted') === '1'; } catch { return false; }
    });
    // Preloaded MP3 sample refs — premium CC-0 sounds from archive.org
    // Creative Dice Roll pack. Falls back to Web Audio synth if the sample
    // can't play (older browsers, autoplay blocked, etc.).
    const sampleRefs = useRef({});

    useEffect(() => {
        const files = {
            dice_roll: '/sounds/dice_roll.mp3',
            dice_settle: '/sounds/dice_settle.mp3',
            chat_ping: '/sounds/chat_ping.mp3',
            house_take: '/sounds/house_take.mp3',
        };
        Object.entries(files).forEach(([k, url]) => {
            const a = new Audio(url);
            a.preload = 'auto';
            a.volume = 0.55;
            sampleRefs.current[k] = a;
        });
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((m) => {
            const next = !m;
            try { localStorage.setItem('sd_muted', next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // ── Sound effects ────────────────────────────────────────────────────
    // Hybrid: preloaded MP3 samples for the flagship moments (dice roll,
    // settle, chat ping, house take), synthesized via Web Audio for the
    // rest. Muted flag from localStorage silences everything.
    const playSfx = useCallback((kind) => {
        if (muted) return;
        // Try MP3 sample first
        const sample = sampleRefs.current[kind];
        if (sample) {
            try {
                sample.currentTime = 0;
                const p = sample.play();
                if (p && typeof p.catch === 'function') p.catch(() => {});
                return;
            } catch { /* fall through to synth */ }
        }
        try {
            if (!audioCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtxRef.current = new Ctx();
            }
            const ac = audioCtxRef.current;
            if (ac.state === 'suspended') ac.resume();
            const now = ac.currentTime;
            const beep = (freq, dur, type = 'square', gain = 0.06, when = 0) => {
                const o = ac.createOscillator();
                const g = ac.createGain();
                o.type = type;
                o.frequency.value = freq;
                g.gain.setValueAtTime(0.0001, now + when);
                g.gain.exponentialRampToValueAtTime(gain, now + when + 0.005);
                g.gain.exponentialRampToValueAtTime(0.0001, now + when + dur);
                o.connect(g); g.connect(ac.destination);
                o.start(now + when); o.stop(now + when + dur + 0.02);
            };
            switch (kind) {
                case 'seven_out':
                    // Deep, sad drop — two low sawtooth pulses
                    beep(220, 0.15, 'sawtooth', 0.09);
                    beep(160, 0.28, 'sawtooth', 0.08, 0.1);
                    beep(110, 0.35, 'sawtooth', 0.06, 0.25);
                    break;
                case 'point_hit':
                    // Triumphant three-note ascent
                    beep(880, 0.1, 'square', 0.07);
                    beep(1174, 0.1, 'square', 0.07, 0.09);
                    beep(1568, 0.2, 'square', 0.08, 0.18);
                    beep(2093, 0.28, 'sine', 0.06, 0.3);
                    break;
                case 'win_flash':
                    // Bright cash-out cascade
                    beep(660, 0.08, 'square', 0.07);
                    beep(880, 0.08, 'square', 0.07, 0.08);
                    beep(1320, 0.12, 'square', 0.08, 0.16);
                    beep(1760, 0.2, 'triangle', 0.07, 0.24);
                    break;
                default: break;
            }
        } catch { /* audio context unavailable — ignore */ }
    }, [muted]);

    // Show a friendly banner if the user landed here via someone's invite link
    useEffect(() => {
        if (inviter && !inviteToastShownRef.current) {
            inviteToastShownRef.current = true;
            toast(`${decodeURIComponent(inviter)} pulled you up to the circle`, {
                icon: '🎲',
                duration: 5000,
            });
        }
    }, [inviter]);

    // If we came in via an invite AND we're not seated yet, auto-open the join dialog
    // once we've loaded the room state.
    useEffect(() => {
        if (!inviter || !room || !user) return;
        if (inviteToastShownRef.current !== true) return;
        const seated = room.seats?.some((s) => s.user_id === user.id);
        if (!seated && !joinOpen) {
            setBuyIn(String(room.min_buy_in));
            setJoinOpen(true);
        }
        // Only run this transition once — clear the flag by flipping the ref
        inviteToastShownRef.current = 'consumed';
    }, [room, user, inviter]);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/street-dice/rooms/${roomId}`, { withCredentials: true });
            setRoom(data);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 404) {
                // Room was closed / purged. Bounce back to lobby.
                toast('This table is closed', { icon: '🚪' });
                nav('/casino/street-dice');
                return;
            }
            if (detail && !status?.toString().startsWith('5')) toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, [roomId, nav]);

    // 🔌 Real-time transport: WebSocket with 5s REST heartbeat fallback and
    // exponential-backoff reconnect. First load is a REST call so we render
    // instantly even if the WS handshake is slow.
    useEffect(() => {
        let mounted = true;
        let heartbeat = null;
        wsClosedByUsRef.current = false;

        // Initial snapshot
        load();

        const connect = () => {
            if (!mounted) return;
            const token = (typeof window !== 'undefined' && localStorage.getItem('access_token')) || '';
            if (!token) return; // AuthContext will kick user out; nothing to connect to
            const wsBase = (process.env.REACT_APP_BACKEND_URL || '')
                .replace(/^http/, 'ws');
            const url = `${wsBase}/api/street-dice/rooms/${roomId}/ws?token=${encodeURIComponent(token)}`;
            let ws;
            try {
                ws = new WebSocket(url);
            } catch {
                scheduleReconnect();
                return;
            }
            wsRef.current = ws;

            ws.onopen = () => {
                if (!mounted) return;
                setWsLive(true);
                wsBackoffRef.current = 1000; // reset backoff
            };
            ws.onmessage = (ev) => {
                if (!mounted) return;
                let msg;
                try { msg = JSON.parse(ev.data); } catch { return; }
                if (msg.type === 'room_state' && msg.room) {
                    setRoom(msg.room);
                    setLoading(false);
                } else if (msg.type === 'roll_start') {
                    // Kick off the tumble animation immediately so every seat
                    // shakes in sync. The final total arrives ~800ms later in
                    // the follow-up room_state, matching the anim duration.
                    setRolling(true);
                    playSfx('dice_roll');
                } else if (msg.type === 'bot_chat') {
                    // Push chat message onto the floating feed and expire
                    // after ~4.5s so the corner stays lively but not spammy.
                    const chat = {
                        id: `${msg.at || Date.now()}-${msg.user_id}-${Math.random().toString(36).slice(2, 6)}`,
                        name: msg.name,
                        text: msg.text,
                        personality: msg.personality,
                    };
                    setChatMessages((prev) => [...prev.slice(-4), chat]);
                    setChatFlash(chat);
                    playSfx('chat_ping');
                    setTimeout(() => {
                        setChatMessages((prev) => prev.filter((c) => c.id !== chat.id));
                    }, 4500);
                    setTimeout(() => {
                        setChatFlash((cur) => (cur && cur.id === chat.id ? null : cur));
                    }, 1800);
                } else if (msg.type === 'chat') {
                    // Human-to-human chat — reuse the same floating feed.
                    // Dedupe self-echoes: if we already optimistically added
                    // this message from THIS user within the last 4s, skip
                    // the server round-trip so it doesn't render twice.
                    if (msg.user_id === user?.id) {
                        const now = Date.now();
                        setChatMessages((prev) => {
                            const dupe = prev.some((c) =>
                                c.self && c.text === msg.text
                                && (now - (c.sentAt || 0)) < 4000
                            );
                            if (dupe) return prev; // already rendered locally
                            return [...prev.slice(-6), {
                                id: `${msg.at || now}-${msg.user_id}-${Math.random().toString(36).slice(2, 6)}`,
                                name: msg.name, text: msg.text, human: true,
                            }];
                        });
                        return; // no flash + no sfx for own echo
                    }
                    const chat = {
                        id: `${msg.at || Date.now()}-${msg.user_id}-${Math.random().toString(36).slice(2, 6)}`,
                        name: msg.name, text: msg.text, human: true,
                    };
                    // @mention self-detection — flash gold + play a soft ding
                    // when someone calls you out. Case-insensitive, matches
                    // display name AND email localpart as a fallback.
                    const mentions = _extractMentions(msg.text || '').map((m) => m.toLowerCase());
                    const myName = (user?.name || '').toLowerCase();
                    const myLocal = (user?.email || '').split('@')[0].toLowerCase();
                    if (mentions.includes(myName) || (myLocal && mentions.includes(myLocal))) {
                        chat.mentioned_you = true;
                        playSfx('win_flash'); // soft chime — reuse the cash cascade
                    }
                    setChatMessages((prev) => [...prev.slice(-6), chat]);
                    setChatFlash(chat);
                    playSfx('chat_ping');
                    setTimeout(() => {
                        setChatMessages((prev) => prev.filter((c) => c.id !== chat.id));
                    }, 8000);
                    setTimeout(() => {
                        setChatFlash((cur) => (cur && cur.id === chat.id ? null : cur));
                    }, 1800);
                } else if (msg.type === 'typing') {
                    // Ignore my own typing echo.
                    if (msg.user_id === user?.id) return;
                    if (msg.typing) {
                        setTypingUsers((prev) => ({
                            ...prev,
                            [msg.user_id]: {
                                name: msg.name,
                                expiresAt: Date.now() + 4500,
                            },
                        }));
                    } else {
                        setTypingUsers((prev) => {
                            const next = { ...prev };
                            delete next[msg.user_id];
                            return next;
                        });
                    }
                } else if (msg.type === 'reaction') {
                    // Tap-to-send trash-talk emote — bursts up over the pit and
                    // fades. Everyone (incl. sender) renders from the WS echo so
                    // all seats see the same burst.
                    const rid = `${msg.at || Date.now()}-${msg.user_id}-${Math.random().toString(36).slice(2, 6)}`;
                    setReactions((prev) => [...prev.slice(-11), {
                        id: rid,
                        emoji: msg.emoji,
                        name: msg.name,
                        isMe: msg.user_id === user?.id,
                        x: 12 + Math.random() * 76, // random horizontal spawn %
                    }]);
                    playSfx('chat_ping');
                    setTimeout(() => {
                        setReactions((prev) => prev.filter((r) => r.id !== rid));
                    }, 2600);
                } else if (msg.type === 'personal_win' && msg.user_id === user?.id) {
                    // Gold-lettered win flash — only fires for the winning seat.
                    // Show NET profit (what they actually won), not the gross
                    // payout which includes their own returned stake.
                    setWinFlash({
                        id: Date.now(),
                        amount: msg.net != null ? msg.net : msg.amount,
                        kind: msg.kind,
                    });
                    playSfx('win_flash');
                    // Wallet-tied: payout just landed in wallet — refresh chip.
                    refreshUser?.();
                    setTimeout(() => setWinFlash(null), 3200);
                } else if (msg.type === 'house_take') {
                    // Table-wide announcement: house pocketed money on this
                    // hand (2/12 come-out rake OR a bot's real-money win).
                    const flash = {
                        id: Date.now(),
                        amount: Number(msg.amount || 0),
                        source: msg.source || 'rake',
                    };
                    setHouseTakeFlash(flash);
                    playSfx('house_take');
                    setTimeout(() => {
                        setHouseTakeFlash((cur) => (cur && cur.id === flash.id ? null : cur));
                    }, 3400);
                } else if (msg.type === 'roll_settled') {
                    // Server hint right after a roll resolves; pick a fitting sfx
                    if (msg.outcome === 'seven_out') playSfx('seven_out');
                    else if (msg.outcome === 'point_hit') playSfx('point_hit');
                    else playSfx('dice_settle');
                } else if (msg.type === 'shooter_timeout') {
                    toast(`Shooter took too long — dice pass on`, { icon: '⏱️' });
                } else if (msg.type === 'force_faded') {
                    // Two audiences on the same event:
                    //   · The faded seat itself → personal toast + wallet refresh
                    //   · Everyone else (bots + spectators) → table-wide banner
                    // Reasons the server may send:
                    //   · "shooter_rolled"    → shooter hit ROLL before you covered
                    //   · "heads_up_timeout"  → 20s heads-up covering window expired
                    const amount = Number(msg.amount || 0);
                    const isSelf = msg.user_id === user?.id;
                    if (isSelf) {
                        const line = msg.reason === 'shooter_rolled'
                            ? `Auto-faded $${amount.toFixed(2)} — the shooter rolled before you covered.`
                            : `Auto-faded $${amount.toFixed(2)} — the 20-second fade window expired.`;
                        toast(line, {
                            icon: '🔒',
                            duration: 6000,
                            description: 'Come-out only. Side bets untouched.',
                        });
                        refreshUser?.(); // wallet just moved — refresh the chip
                    }
                    // Table-wide banner — everyone (including the faded seat)
                    // sees the same play-by-play so the room narrative stays
                    // consistent. Uses the shorthand player name from the WS.
                    const flash = {
                        id: Date.now(),
                        name: msg.display_name || 'Player',
                        amount,
                        reason: msg.reason,
                        self: isSelf,
                    };
                    setForceFadeFlash(flash);
                    setTimeout(() => {
                        setForceFadeFlash((cur) => (cur && cur.id === flash.id ? null : cur));
                    }, 3800);
                } else if (msg.type === 'idle_kick' && msg.user_id === user?.id) {
                    toast.error('You were removed for inactivity');
                } else if (msg.type === 'room_closed') {
                    const reason = msg.reason === 'closed_by_host'
                        ? `${msg.by || 'Host'} closed the table`
                        : 'Table closed';
                    toast(reason, { icon: '🚪' });
                    wsClosedByUsRef.current = true;
                    setTimeout(() => nav('/casino/street-dice'), 1200);
                } else if (msg.type === 'ping') {
                    // Server keep-alive — ignore.
                }
            };
            ws.onclose = () => {
                if (!mounted) return;
                setWsLive(false);
                if (!wsClosedByUsRef.current) scheduleReconnect();
            };
            ws.onerror = () => {
                // onclose will fire right after — reconnect there.
            };
        };

        const scheduleReconnect = () => {
            const delay = wsBackoffRef.current;
            wsBackoffRef.current = Math.min(delay * 2, 10000);
            setTimeout(() => { if (mounted) connect(); }, delay);
        };

        connect();

        // Client-side ping every 20s — many mobile browsers close idle WS
        // silently. Sending any frame keeps the socket warm on our side AND
        // the operator's ingress.
        const clientPing = setInterval(() => {
            const ws = wsRef.current;
            if (ws && ws.readyState === 1) {
                try { ws.send('ping'); } catch { /* ignore */ }
            }
        }, 20000);

        // Reconnect immediately when the user comes back to the tab. Mobile
        // Safari + Chrome throttle background tabs and often kill sockets;
        // without this the fader falls behind and has to hard-refresh.
        const onVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            const ws = wsRef.current;
            if (!ws || ws.readyState !== 1) {
                wsBackoffRef.current = 1000;
                connect();
            }
            // Force a fresh REST snapshot regardless — belt-and-suspenders.
            load();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onVisibility);

        // Always-on 5s REST refresh. Even when WS is live, phones sometimes
        // silently miss frames — a 5s poll guarantees the fader never sees a
        // stale roll. Total network cost is trivial (~200 bytes / 5s).
        heartbeat = setInterval(() => load(), 5000);

        return () => {
            mounted = false;
            wsClosedByUsRef.current = true;
            if (heartbeat) clearInterval(heartbeat);
            clearInterval(clientPing);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onVisibility);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch { /* ignore */ }
                wsRef.current = null;
            }
        };
    }, [roomId, load]);

    // Fire a celebration whenever a new roll lands
    useEffect(() => {
        if (!room?.hand?.rolls?.length) return;
        const rolls = room.hand.rolls;
        const latest = rolls[rolls.length - 1];
        if (latest.roll_id === lastRollIdRef.current) return;
        lastRollIdRef.current = latest.roll_id;

        setRolling(true);
        setTimeout(() => {
            setRolling(false);
            const out = latest.outcome;
            if (out === 'natural') setCelebration({ title: 'NATURAL!', sub: `Shooter wins on ${latest.total}`, tone: 'win' });
            else if (out === 'craps') setCelebration({ title: 'CRAPS', sub: `${latest.total} · shooter down`, tone: 'lose' });
            else if (out === 'point_set') setCelebration({ title: `POINT ${latest.total}`, sub: 'Chase it now', tone: 'point' });
            else if (out === 'point_hit') setCelebration({ title: 'HIT THE POINT!', sub: `${latest.total} · shooter wins`, tone: 'win' });
            else if (out === 'seven_out') setCelebration({ title: 'SEVEN OUT', sub: 'Line down · new shooter', tone: 'lose' });
            else if (out === 'no_decision') setCelebration({ title: `${latest.total}`, sub: 'Keep rolling', tone: 'neutral' });
            setTimeout(() => setCelebration(null), 1600);
            refreshUser?.();
        }, 500);
    }, [room?.hand?.rolls, refreshUser]);

    const mySeat = useMemo(
        () => room?.seats?.find((s) => s.user_id === user?.id) || null,
        [room, user],
    );
    const isSeated = !!mySeat;
    const isShooter = room?.shooter_id === user?.id;
    const phase = room?.phase || 'waiting';
    const hand = room?.hand;
    const totalCovered = useMemo(
        () => (hand?.coverages || []).reduce((s, c) => s + Number(c.amount || 0), 0),
        [hand],
    );
    const uncovered = hand ? Math.max(0, Number(hand.main_wager || 0) - totalCovered) : 0;

    // ─── Low-balance nudge ──────────────────────────────────────────────
    // In real-money rooms, if the player's playable wallet balance dips
    // below the room's minimum wager, gently point them at the WALLET chip
    // (which opens the Square receipt top-up sheet). Fires once per dip —
    // resets when the balance climbs back above min_wager, or once the
    // top-up sheet is opened.
    const playableWallet = useMemo(
        () => Number(user?.credits ?? 0) + Number(user?.bonus_credits ?? 0),
        [user?.credits, user?.bonus_credits],
    );
    useEffect(() => {
        if (!room || room.mode !== 'real' || !isSeated) return;
        const minWager = Number(room.min_wager || 0);
        if (!minWager) return;

        if (playableWallet >= minWager) {
            // Recovered — arm the warning for the next dip.
            if (lowBalanceShownRef.current && lowBalanceToastIdRef.current) {
                toast.dismiss(lowBalanceToastIdRef.current);
                lowBalanceToastIdRef.current = null;
            }
            lowBalanceShownRef.current = false;
            return;
        }

        // Below minimum. Fire once (debounced by the ref) and skip when
        // the top-up sheet is already open so we don't nag over the modal.
        if (lowBalanceShownRef.current || topUpOpen) return;
        lowBalanceShownRef.current = true;
        lowBalanceToastIdRef.current = toast(
            `You're below the ${fmt$(minWager)} minimum — tap WALLET to top up`,
            {
                icon: '💸',
                duration: 8000,
                action: {
                    label: 'Top up',
                    onClick: () => setTopUpOpen(true),
                },
            },
        );
    }, [playableWallet, room, isSeated, topUpOpen]);

    // ─── "Welcome back from PayPal" pulse ──────────────────────────────
    // If the player has tapped the PayPal button and then leaves this tab
    // (to complete the payment), gently pulse the receipt uploader in gold
    // AND fire the OS file/camera picker the moment they come back — turns
    // "pay then post" into a single flow. Fires once per return visit;
    // auto-clears after ~2s.
    useEffect(() => {
        if (!topUpOpen || !topUpPayPalOpened) return;
        const onVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            setTopUpReturnPulse(true);
            // Small delay so the dialog is settled and the tap isn't eaten
            // by the browser's tab-switch animation.
            setTimeout(() => {
                try { topUpFileRef.current?.click(); } catch { /* ignore */ }
            }, 500);
            setTimeout(() => setTopUpReturnPulse(false), 2200);
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [topUpOpen, topUpPayPalOpened]);

    // ─── Actions ────────────────────────────────────────────────────────

    const join = async () => {
        const isRealMode = room?.mode === 'real';
        const amt = parseFloat(buyIn);
        // Real-money rooms are wallet-tied — no buy-in needed. Practice
        // rooms still take a fake-money buy-in to seed the fun-chip stack.
        if (!isRealMode) {
            if (!amt || amt <= 0) return toast.error('Enter a buy-in');
            if (amt < room.min_buy_in || amt > room.max_buy_in) {
                return toast.error(`Buy-in must be ${fmt$(room.min_buy_in)} – ${fmt$(room.max_buy_in)}`);
            }
        }
        setBusy(true);
        try {
            const payload = isRealMode ? {} : { buy_in: amt };
            // Private rooms require the join code. Read from URL first,
            // otherwise fall back to whatever the server already knows (creator).
            if (room.is_private) {
                payload.join_code = (urlJoinCode || room.join_code || '').toUpperCase();
            }
            // Host attribution — earns the creator a $5 credit when a brand
            // new signup plays their first hand from this invite link.
            if (urlHostId) {
                payload.host_id = urlHostId;
            }
            const { data } = await axios.post(`${API}/street-dice/rooms/${roomId}/join`, payload, { withCredentials: true });
            setRoom(data);
            setJoinOpen(false);
            toast.success(isRealMode
                ? 'Took a seat — wagers pull straight from your wallet'
                : `Took a seat — ${fmt$(amt)} on the felt`);
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not join');
        } finally {
            setBusy(false);
        }
    };

    const leave = async () => {
        if (!isSeated) return;
        if (hand && (isShooter || (hand.coverages || []).some((c) => c.user_id === user.id))) {
            toast.error("Can't leave mid-hand — finish this one first.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/street-dice/rooms/${roomId}/leave`, {}, { withCredentials: true });
            refreshUser?.();
            nav('/casino/street-dice');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not leave');
        } finally {
            setBusy(false);
        }
    };

    const announce = async (overrideAmount) => {
        const raw = overrideAmount != null ? overrideAmount : announceAmt;
        const amt = parseFloat(raw);
        if (!amt || amt <= 0) return toast.error('Announce a wager amount');
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/${roomId}/announce`,
                { amount: amt },
                { withCredentials: true },
            );
            setRoom(data);
            setAnnounceAmt('');
            // Remember the amount so the next hand pre-fills / offers REPEAT.
            setLastAnnouncedAmt(String(amt));
            try { localStorage.setItem('street-dice-last-wager', String(amt)); } catch { /* ignore */ }
            // Wallet-tied: wager pulled straight from wallet, refresh the chip.
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not announce');
        } finally {
            setBusy(false);
        }
    };

    const cover = async (overrideAmount) => {
        const raw = overrideAmount != null ? overrideAmount : coverAmt;
        const amt = parseFloat(raw);
        if (!amt || amt <= 0) return toast.error('Enter an amount to fade');
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/${roomId}/cover`,
                { amount: amt },
                { withCredentials: true },
            );
            setRoom(data);
            setCoverAmt('');
            toast.success(`Faded ${fmt$(amt)}`);
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not fade');
        } finally {
            setBusy(false);
        }
    };

    const roll = async () => {
        if (!isShooter) return;
        setBusy(true);
        setRolling(true); // immediate feedback for the shooter; roll_start covers observers
        try {
            const { data } = await axios.post(`${API}/street-dice/rooms/${roomId}/roll`, {}, { withCredentials: true });
            setRoom(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Roll failed');
            setRolling(false);
        } finally {
            setBusy(false);
        }
    };

    // Utility for @mention detection: pull all @Name tokens from a chat body.
    const _extractMentions = (text) => {
        if (!text) return [];
        // Word chars + up to 40 to accommodate real display names.
        const re = /@([A-Za-z0-9_\-.]{1,40})/g;
        const out = [];
        let m;
        while ((m = re.exec(text)) !== null) out.push(m[1]);
        return out;
    };

    // Human seat names for @mention autocomplete (self excluded)
    const mentionableNames = useMemo(() => (
        (room?.seats || [])
            .filter((s) => !s.is_bot && s.user_id !== user?.id)
            .map((s) => s.name)
            .filter(Boolean)
    ), [room?.seats, user?.id]);

    const mentionSuggestions = useMemo(() => {
        if (!mentionQuery) return [];
        const q = (mentionQuery.prefix || '').toLowerCase();
        return mentionableNames
            .filter((n) => n.toLowerCase().startsWith(q))
            .slice(0, 5);
    }, [mentionQuery, mentionableNames]);

    // Detect an active @-token at the caret. Called from chat input onChange.
    const _detectMentionAtCaret = (text, caret) => {
        // Walk back from caret to the last @ (or space/newline).
        let i = caret - 1;
        while (i >= 0 && /[A-Za-z0-9_\-.]/.test(text[i])) i--;
        if (i < 0 || text[i] !== '@') return null;
        // Must be at word boundary: preceded by whitespace or start of string.
        if (i > 0 && !/\s/.test(text[i - 1])) return null;
        return { start: i, prefix: text.slice(i + 1, caret) };
    };

    const insertMention = (name) => {
        if (!mentionQuery) return;
        const { start } = mentionQuery;
        const caret = chatInputRef.current?.selectionStart ?? chatDraft.length;
        const before = chatDraft.slice(0, start);
        const after = chatDraft.slice(caret);
        const next = `${before}@${name} ${after}`;
        setChatDraft(next);
        setMentionQuery(null);
        setTimeout(() => {
            if (!chatInputRef.current) return;
            const pos = (before + '@' + name + ' ').length;
            chatInputRef.current.focus();
            chatInputRef.current.setSelectionRange(pos, pos);
        }, 0);
    };

    // Send a typing ping (throttled — max once every 3s per user session).
    // Server broadcasts to all WS subscribers, receivers add the user to
    // a typingUsers map with a 4.5s expiration.
    const sendTypingPing = useCallback(() => {
        if (!isSeated) return;
        const now = Date.now();
        if (now - typingSentAtRef.current < 3000) return;
        typingSentAtRef.current = now;
        axios.post(
            `${API}/street-dice/rooms/${roomId}/typing`,
            { typing: true },
            { withCredentials: true },
        ).catch(() => { /* typing pings are fire-and-forget */ });
        // Reset the "typing is over" timer: if user goes idle for 4s, emit
        // a typing:false so peers immediately clear the pill.
        if (typingClearTimerRef.current) clearTimeout(typingClearTimerRef.current);
        typingClearTimerRef.current = setTimeout(() => {
            axios.post(
                `${API}/street-dice/rooms/${roomId}/typing`,
                { typing: false },
                { withCredentials: true },
            ).catch(() => {});
            typingSentAtRef.current = 0;
        }, 4000);
    }, [isSeated, roomId]);

    // Sweep expired typers every second so the pill vanishes if a peer
    // stops typing without sending us a typing:false (network drop, etc.).
    useEffect(() => {
        const t = setInterval(() => {
            setTypingUsers((prev) => {
                const now = Date.now();
                const next = {};
                let changed = false;
                for (const [uid, v] of Object.entries(prev)) {
                    if (v.expiresAt > now) next[uid] = v;
                    else changed = true;
                }
                return changed ? next : prev;
            });
        }, 1000);
        return () => clearInterval(t);
    }, []);

    const sendChat = async (override) => {
        const text = (override != null ? override : chatDraft).trim();
        if (!text || !isSeated) return;
        if (override == null) setChatDraft('');
        // Optimistic local push — the sender sees their message the instant
        // they hit send. When the WS echo arrives (usually within <200ms),
        // the incoming-message handler dedupes on user_id + text + recency.
        const chat = {
            id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: user?.name || user?.email?.split('@')[0] || 'You',
            text,
            human: true,
            self: true,
            sentAt: Date.now(),
        };
        setChatMessages((prev) => [...prev.slice(-6), chat]);
        setChatFlash(chat);
        playSfx('chat_ping');
        setTimeout(() => {
            setChatMessages((prev) => prev.filter((c) => c.id !== chat.id));
        }, 8000);
        setTimeout(() => {
            setChatFlash((cur) => (cur && cur.id === chat.id ? null : cur));
        }, 1800);
        try {
            await axios.post(
                `${API}/street-dice/rooms/${roomId}/chat`,
                { text },
                { withCredentials: true },
            );
        } catch (err) {
            // Rollback the optimistic message so the sender isn't misled.
            setChatMessages((prev) => prev.filter((c) => c.id !== chat.id));
            toast.error(err?.response?.data?.detail || 'Chat send failed');
        }
    };

    // Tap-to-send trash-talk emote. Throttled to 1 per 800ms so a mashing
    // thumb can't flood the pit. Fire-and-forget — the burst renders from the
    // WS echo (fast enough to feel instant) so every seat stays in sync.
    const sendReaction = (emoji) => {
        if (!isSeated) return;
        const now = Date.now();
        if (now - reactionSentAtRef.current < 800) return;
        reactionSentAtRef.current = now;
        axios.post(
            `${API}/street-dice/rooms/${roomId}/reaction`,
            { emoji },
            { withCredentials: true },
        ).catch(() => { /* reactions are fire-and-forget hype */ });
    };

    const submitTopUp = async (file) => {
        if (!file) return;
        setTopUpBusy(true);
        try {
            const form = new FormData();
            form.append('receipt', file);
            if (topUpClaimed && parseFloat(topUpClaimed) > 0) {
                form.append('claimed_amount', String(parseFloat(topUpClaimed)));
            }
            form.append('note', `Uploaded from Street Dice room ${roomId}`);
            const { data } = await axios.post(
                `${API}/square-receipts/upload`,
                form,
                {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                },
            );
            if (data?.status === 'approved' || data?.credited_amount > 0) {
                toast.success(`Wallet topped up — ${fmt$(data.credited_amount || 0)} added`);
            } else {
                toast('Receipt uploaded — under review', { icon: '🧾' });
            }
            setTopUpOpen(false);
            setTopUpClaimed('');
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed');
        } finally {
            setTopUpBusy(false);
        }
    };

    const shareRoom = async () => {
        const displayName = user?.name || user?.email?.split('@')[0] || 'A friend';
        const codeSuffix = room?.is_private && room?.join_code
            ? `&code=${encodeURIComponent(room.join_code)}` : '';
        // host= param drives the host-reward: whoever created this room gets
        // credit when a brand-new signup arrives via this link and plays.
        const hostSuffix = room?.created_by ? `&host=${encodeURIComponent(room.created_by)}` : '';
        const inviteUrl = `${window.location.origin}/casino/street-dice/${roomId}?invite=${encodeURIComponent(displayName)}${codeSuffix}${hostSuffix}`;
        const codeLine = room?.is_private && room?.join_code
            ? ` Private code: ${room.join_code}.` : '';
        const text = `${displayName} pulled up a Street Dice circle on BETohalics — take a seat.${codeLine}`;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Street Dice — ${room?.name || 'the circle'}`,
                    text,
                    url: inviteUrl,
                });
                return;
            }
        } catch (err) {
            if (err?.name === 'AbortError') return; // user cancelled share sheet
        }
        try {
            await navigator.clipboard.writeText(inviteUrl);
            toast.success('Invite link copied — share it with a friend');
        } catch {
            toast.error('Could not open share sheet');
        }
    };

    const closeRoom = async () => {
        if (!window.confirm('Close this table? Any remaining seated humans will be refunded and everyone kicked back to the lobby.')) return;
        setBusy(true);
        try {
            await axios.post(`${API}/street-dice/rooms/${roomId}/close`, {}, { withCredentials: true });
            toast.success('Table closed');
            nav('/casino/street-dice');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not close the table');
        } finally {
            setBusy(false);
        }
    };

    // ─── Side-bet actions ─────────────────────────────────────────────
    const postSideBet = async (type, stake) => {
        const amt = parseFloat(stake);
        if (!amt || amt <= 0) return toast.error('Enter a stake');
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/${roomId}/side-bets`,
                { type, stake: amt },
                { withCredentials: true },
            );
            setRoom(data);
            toast.success(`Challenge posted: ${type.replace('_', ' ')} — $${amt.toFixed(2)}`);
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not post challenge');
        } finally {
            setBusy(false);
        }
    };

    const acceptSideBet = async (sb) => {
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/${roomId}/side-bets/accept`,
                { side_bet_id: sb.id },
                { withCredentials: true },
            );
            setRoom(data);
            toast.success(`Fading ${sb.poster_name}'s challenge for $${parseFloat(sb.poster_stake).toFixed(2)}`);
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not accept');
        } finally {
            setBusy(false);
        }
    };

    const cancelSideBet = async (sb) => {
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/street-dice/rooms/${roomId}/side-bets/${sb.id}/cancel`,
                {},
                { withCredentials: true },
            );
            setRoom(data);
            toast.success('Challenge cancelled — refunded');
            refreshUser?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not cancel');
        } finally {
            setBusy(false);
        }
    };

    if (loading || !room) {
        return (
            <div className="min-h-screen text-white flex items-center justify-center" style={{ background: theme.pageBg }}>
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const seats = room.seats || [];
    const lastRoll = hand?.rolls?.[hand.rolls.length - 1];

    return (
        <div className="a11y-font min-h-screen text-white relative" style={{ background: theme.pageBg }} data-testid="street-dice-room">
            <ThemeOverlay theme={theme} />
            <Header />
            <div className="relative max-w-md mx-auto px-3 pt-4 pb-40">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => nav('/casino/street-dice')}
                        className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white"
                        data-testid="street-dice-room-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Lobby
                    </button>
                    <div className="text-center min-w-0 px-2">
                        <div className="flex items-center justify-center gap-2">
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                    background: wsLive ? '#22c55e' : '#71717a',
                                    boxShadow: wsLive ? '0 0 8px #22c55e' : 'none',
                                }}
                                title={wsLive ? 'Live connection' : 'Reconnecting…'}
                                data-testid={wsLive ? 'street-dice-ws-live' : 'street-dice-ws-offline'}
                            />
                            <h1
                                className="text-xl sm:text-2xl font-black text-white truncate"
                                style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                            >
                                {room.name}
                            </h1>
                        </div>
                        <p className="text-[10px] font-mono text-white/50 mt-0.5">
                            {room.mode === 'real'
                                ? <span className="text-[#FFD700]">REAL $ · </span>
                                : <span>PRACTICE · </span>}
                            wager {fmt$(room.min_wager)}–{fmt$(room.max_wager)}
                        </p>
                        {room.is_private && room.join_code && (
                            <button
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(room.join_code);
                                        toast.success(`Code ${room.join_code} copied — share it with your crew`);
                                    } catch {
                                        toast(`Join code: ${room.join_code}`, { icon: '🔑' });
                                    }
                                }}
                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-[10px] font-mono tracking-[0.3em] text-white/80"
                                title="Copy join code"
                                data-testid="street-dice-room-join-code"
                            >
                                🔒 {room.join_code}
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={toggleMute}
                            className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition"
                            title={muted ? 'Unmute room' : 'Mute room'}
                            data-testid="street-dice-room-mute"
                        >
                            {muted
                                ? <VolumeX className="w-4 h-4" />
                                : <Volume2 className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={shareRoom}
                            className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition"
                            title="Invite a friend to this circle"
                            data-testid="street-dice-room-share"
                        >
                            <Share2 className="w-4 h-4" />
                        </button>
                        {(room.created_by === user?.id || user?.role === 'admin') && (
                            <button
                                onClick={closeRoom}
                                className="p-2 rounded-md hover:bg-red-500/10 text-white/60 hover:text-red-400 transition"
                                title="Close this table"
                                data-testid="street-dice-room-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => setTheme(shuffleTheme(theme.id))}
                            className="p-2 rounded-md hover:bg-white/5 text-white/60"
                            title="Shuffle theme"
                            data-testid="street-dice-room-shuffle-theme"
                        >
                            <Palette className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setRulesOpen(true)}
                            className="p-2 rounded-md hover:bg-white/5 text-white/60"
                            data-testid="street-dice-room-rules"
                        >
                            <HelpCircle className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* STREET DICE brand banner — official Betohalics logo + graffiti wordmark */}
                <div className="flex flex-col items-center -mt-1 mb-1.5 select-none" data-testid="street-dice-brand-banner">
                    <img
                        src="/street-dice/betohalics-logo.png"
                        alt="Betohalics"
                        className="h-7 sm:h-9 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
                        draggable={false}
                    />
                    <div className="flex items-center justify-center gap-1.5 leading-none mt-0.5">
                        <Dice5 className="w-5 h-5 text-[#FFD700] drop-shadow-[0_0_10px_rgba(255,215,0,0.6)] rotate-[-12deg]" />
                        <h1
                            className="text-2xl sm:text-3xl font-black tracking-tight"
                            style={{
                                fontFamily: '"Bebas Neue","Impact",sans-serif',
                                letterSpacing: '0.04em',
                                background: 'linear-gradient(180deg,#FFF2B0 0%,#FFD700 45%,#C98A1A 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))',
                            }}
                        >
                            STREET DICE
                        </h1>
                        <Dice5 className="w-5 h-5 text-[#FFD700] drop-shadow-[0_0_10px_rgba(255,215,0,0.6)] rotate-[12deg]" />
                    </div>
                </div>

                {/* Real-money warning banner */}
                {room.mode === 'real' && (
                    <div
                        className="mb-3 rounded-lg border-2 px-3 py-2 flex items-center gap-2 text-xs"
                        style={{ borderColor: '#FFD70066', background: 'rgba(255,215,0,0.08)' }}
                        data-testid="street-dice-room-real-banner"
                    >
                        <Coins className="w-4 h-4 text-[#FFD700] shrink-0" />
                        <span className="text-white/85">
                            <b className="text-[#FFD700]">Real money.</b> Every wager pulls straight from your wallet — wins land right back in it.
                        </span>
                    </div>
                )}

                {/* Wallet + point strip. In real-money rooms the "stack" IS
                    your live wallet playable balance — read from AuthContext
                    so WS room_state broadcasts (which don't include a
                    per-user wallet field) don't clobber it back to $0. */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                    <StatChip
                        label={hand ? 'MAIN WAGER' : 'PHASE'}
                        value={hand ? fmt$(hand.main_wager) : phase.toUpperCase()}
                        color={theme.potColor}
                        theme={theme}
                        testid="street-dice-main-wager"
                    />
                    <StatChip
                        label={room.point ? `POINT ${room.point}` : 'POINT'}
                        value={room.point ? String(room.point) : 'OFF'}
                        color={room.point ? theme.accent : '#FF3B30'}
                        theme={theme}
                        testid="street-dice-point"
                    />
                </div>

                {/* Full-width wallet bar directly above the table. In real-money
                    mode it's a green "TAP TO ADD" rail that opens the top-up sheet;
                    in practice it just shows YOUR STACK. */}
                <button
                    type="button"
                    onClick={() => { if (room.mode === 'real') setTopUpOpen(true); }}
                    disabled={room.mode !== 'real'}
                    data-testid="street-dice-wallet-bar"
                    className={`w-full mb-2 flex items-center justify-between rounded-xl px-4 py-2.5 border transition active:scale-[0.99] ${
                        room.mode === 'real'
                            ? 'cursor-pointer hover:brightness-110'
                            : 'cursor-default'
                    }`}
                    style={room.mode === 'real' ? {
                        background: 'linear-gradient(90deg, rgba(34,197,94,0.28), rgba(22,163,74,0.14))',
                        borderColor: 'rgba(34,197,94,0.75)',
                        boxShadow: '0 0 18px rgba(34,197,94,0.35)',
                    } : {
                        background: 'rgba(255,255,255,0.05)',
                        borderColor: 'rgba(255,255,255,0.12)',
                    }}
                >
                    <span className="flex items-center gap-2">
                        {room.mode === 'real' && (
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#22c55e] text-black font-black text-sm leading-none">+</span>
                        )}
                        <span
                            className="text-[11px] font-black tracking-widest"
                            style={{ color: room.mode === 'real' ? '#4ade80' : 'rgba(255,255,255,0.6)' }}
                        >
                            {room.mode === 'real' ? 'WALLET · TAP TO ADD' : 'YOUR STACK'}
                        </span>
                    </span>
                    <span
                        className="text-lg font-black tabular-nums"
                        style={{ color: room.mode === 'real' ? '#ffffff' : theme.accent }}
                        data-testid="street-dice-my-stack"
                    >
                        {room.mode === 'real'
                            ? fmt$(Number(user?.credits ?? 0) + Number(user?.bonus_credits ?? 0))
                            : (mySeat ? fmt$(mySeat.room_balance) : '—')}
                    </span>
                </button>

                {/* ── STREET PIT STAGE — cinematic scene is the hero. Players
                    stand around a dark central pit; dice land in the middle. ── */}
                <div
                    className="relative w-full mx-auto my-1 rounded-2xl overflow-hidden border border-[#FFD700]/30 shadow-[0_0_40px_rgba(0,0,0,0.75)]"
                    style={{
                        aspectRatio: '1 / 1',
                        maxWidth: 'min(74vh, 26rem)',
                    }}
                    data-testid="street-dice-stage"
                >
                    {/* brightened, punchy street scene (filter only on this bg layer) */}
                    <div className="absolute inset-0"
                        style={{
                            backgroundImage: 'url(/street-dice/pit-scene.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center 30%',
                            filter: 'brightness(1.22) contrast(1.18) saturate(1.3)',
                        }} />
                    {/* minimal wash — darken top for players + fade to controls */}
                    <div className="absolute inset-0 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 24%, rgba(0,0,0,0) 64%, rgba(5,3,0,0.96) 100%)' }} />
                    <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 110px rgba(0,0,0,0.7)' }} />

                    {/* Player cards — floating AROUND the pit (shooter front/center) */}
                    {(() => {
                        const POS = [
                            { left: '20%', top: '5%' },
                            { right: '20%', top: '5%' },
                            { left: '4%', top: '24%' },
                            { right: '4%', top: '24%' },
                            { left: '3%', top: '54%' },
                            { right: '3%', top: '54%' },
                            { left: '50%', top: '2%', transform: 'translateX(-50%)' },
                        ];
                        const others = seats.filter((s) => room.shooter_id !== s.user_id);
                        const shooterSeat = seats.find((s) => room.shooter_id === s.user_id);
                        const renderCard = (s, style, front) => {
                            const isSh = room.shooter_id === s.user_id;
                            const isMe = s.user_id === user?.id;
                            const isFader = (hand?.coverages || []).some((c) => c.user_id === s.user_id);
                            const initial = (s.name || '?').charAt(0).toUpperCase();
                            return (
                                <div key={s.user_id} className="absolute flex flex-col items-center z-20" style={style} data-testid={`street-dice-player-${s.user_id}`}>
                                    {isSh && <Crown className="w-5 h-5 text-[#FFD700] -mb-1 drop-shadow-[0_0_8px_rgba(255,215,0,1)]" fill="#FFD700" />}
                                    <div
                                        className="rounded-full flex items-center justify-center font-black border-2"
                                        style={{
                                            width: front ? 52 : 42, height: front ? 52 : 42, fontSize: front ? 18 : 15,
                                            borderColor: isSh ? '#FFD700' : isFader ? '#FF3B30' : 'rgba(255,255,255,0.45)',
                                            background: isSh ? 'linear-gradient(135deg,#FFE680,#C98A1A)' : 'rgba(10,8,4,0.78)',
                                            color: isSh ? '#000' : '#fff',
                                            boxShadow: isSh ? '0 0 20px rgba(255,215,0,0.85)' : isFader ? '0 0 14px rgba(255,59,48,0.6)' : '0 3px 12px rgba(0,0,0,0.8)',
                                        }}
                                    >
                                        {s.is_bot ? <Bot className={front ? 'w-6 h-6' : 'w-5 h-5'} /> : initial}
                                    </div>
                                    <div className="mt-1 px-2 py-0.5 rounded-md text-center max-w-[92px]"
                                        style={{ background: 'rgba(0,0,0,0.78)', border: `1px solid ${isSh ? 'rgba(255,215,0,0.5)' : isFader ? 'rgba(255,59,48,0.5)' : 'rgba(255,255,255,0.12)'}` }}>
                                        <div className="text-[11px] font-bold text-white truncate leading-tight">{s.name}{isMe ? ' (you)' : ''}</div>
                                        <div className="text-[10px] font-mono font-bold text-[#FFD700] leading-tight">{fmt$(s.room_balance)}</div>
                                    </div>
                                    {isFader && <span className="mt-0.5 text-[8px] font-black tracking-widest text-[#FF3B30]">FADER</span>}
                                </div>
                            );
                        };
                        return (
                            <>
                                {others.slice(0, 7).map((s, i) => renderCard(s, { position: 'absolute', ...POS[i] }))}
                                {shooterSeat && renderCard(shooterSeat, { left: '50%', bottom: '1%', transform: 'translateX(-50%)' }, true)}
                            </>
                        );
                    })()}

                    {/* OPEN SEAT flank (left) */}
                    {!isSeated && seats.length < 8 && (
                        <button
                            type="button"
                            onClick={() => { setBuyIn(String(room.min_buy_in)); setJoinOpen(true); }}
                            className="absolute left-1/2 -translate-x-1/2 top-2 z-30 flex items-center gap-1 rounded-full border border-dashed border-[#FFD700]/60 bg-black/65 backdrop-blur-sm px-3 py-1 text-[#FFD700] hover:bg-black/85 active:scale-95 transition"
                            data-testid="street-dice-open-seat-btn"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-black tracking-widest">TAKE OPEN SEAT</span>
                        </button>
                    )}

                    {/* Central dice PIT — realistic rectangular throwing surface (no circle) */}
                    <div
                        className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 w-[82%] rounded-[26px] flex flex-col items-center justify-start pt-[9%] overflow-hidden z-10"
                        style={{
                            aspectRatio: '16 / 11',
                            border: '1px solid rgba(255,215,0,0.22)',
                            background: 'radial-gradient(ellipse at 50% 36%, #1a1309 0%, #0b0803 62%, #050300 100%)',
                            boxShadow: 'inset 0 8px 32px rgba(0,0,0,0.92), inset 0 -5px 22px rgba(0,0,0,0.85), 0 20px 46px rgba(0,0,0,0.85)',
                        }}
                        data-testid="street-dice-circle"
                    >
                        {/* gritty asphalt speckle + gold crown marking */}
                        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)', backgroundSize: '7px 7px' }} />
                        <Crown className="absolute w-24 h-24 text-[#FFD700] opacity-[0.14] pointer-events-none" fill="#FFD700" />
                        <div className="flex items-center gap-2 relative z-10">
                            <Die value={lastRoll?.die1} rolling={rolling} theme={theme} />
                            <Die value={lastRoll?.die2} rolling={rolling} theme={theme} />
                        </div>
                        {lastRoll && !rolling && !celebration && (
                            <div className="mt-1.5 text-center">
                                <div
                                    className="text-3xl font-black"
                                    style={{ color: theme.accent, fontFamily: theme.headingFont, textShadow: `0 0 12px ${theme.glow}` }}
                                    data-testid="street-dice-last-total"
                                >
                                    {lastRoll.total}
                                </div>
                            </div>
                        )}
                        {hand && !celebration && (
                            <div className="mt-1 text-[10px] font-mono text-white/70 text-center">
                                covered <span style={{ color: theme.potColor }}>{fmt$(totalCovered)}</span>
                                {uncovered > 0 && (
                                    <> · uncov <span className="text-[#FF3B30]">{fmt$(uncovered)}</span></>
                                )}
                            </div>
                        )}

                        <AnimatePresence>
                            {celebration && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.6, y: 10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="absolute inset-x-0 top-[54%] -translate-y-1/2 flex items-center justify-center z-40 px-3"
                                    data-testid={`street-dice-celebration-${celebration.tone}`}
                                >
                                    <div
                                        className="rounded-xl px-4 py-2 border-2 backdrop-blur-sm text-center"
                                        style={{
                                            background: celebration.tone === 'win' ? `${theme.accent}dd` : celebration.tone === 'lose' ? 'rgba(120,0,0,0.85)' : 'rgba(0,0,0,0.7)',
                                            borderColor: celebration.tone === 'win' ? theme.accent : celebration.tone === 'lose' ? '#FF3B30' : theme.accentSoft,
                                            color: celebration.tone === 'win' ? '#000' : '#fff',
                                            boxShadow: `0 0 30px ${theme.glow}`,
                                        }}
                                    >
                                        <div
                                            className="text-xl md:text-2xl font-black leading-none flex items-center gap-2"
                                            style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                                        >
                                            {celebration.tone === 'win' && <Trophy className="w-5 h-5" />}
                                            {celebration.tone === 'lose' && <Skull className="w-5 h-5" />}
                                            {celebration.tone === 'point' && <Sparkles className="w-5 h-5" />}
                                            {celebration.title}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-widest mt-1 opacity-90">
                                            {celebration.sub}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Side Bets drawer */}
                <div id="street-dice-side-bets">
                <SideBetsPanel
                    room={room}
                    user={user}
                    isSeated={isSeated}
                    theme={theme}
                    busy={busy}
                    onPost={postSideBet}
                    onAccept={acceptSideBet}
                    onCancel={cancelSideBet}
                    postOpen={sideBetPostOpen}
                    setPostOpen={setSideBetPostOpen}
                />
                </div>

                {/* History rail */}
                {(room.history || []).length > 0 && (
                    <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3">
                        <p className="text-[9px] tracking-widest uppercase text-white/50 font-black mb-1.5">Recent hands</p>
                        <div className="flex flex-wrap gap-1.5">
                            {(room.history || []).slice(-8).reverse().map((h, i) => (
                                <span
                                    key={i}
                                    className="text-[10px] px-2 py-1 rounded font-mono"
                                    style={{
                                        background: 'rgba(255,255,255,0.05)',
                                        color: (h.outcome === 'natural' || h.outcome === 'point_hit') ? theme.accent : '#FF3B30',
                                    }}
                                    data-testid={`street-dice-history-${i}`}
                                >
                                    {h.total} · {h.outcome}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Gold-lettered personal win flash — only shows for the winner */}
            <AnimatePresence>
                {winFlash && (
                    <motion.div
                        key={winFlash.id}
                        initial={{ opacity: 0, scale: 0.5, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -20 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 flex items-center justify-center z-40 pointer-events-none"
                        data-testid="street-dice-win-flash"
                    >
                        <div className="text-center">
                            <div
                                className="text-[10px] uppercase tracking-[0.4em] font-black mb-1"
                                style={{ color: '#FFD700' }}
                            >
                                YOU WON
                            </div>
                            <div
                                className="text-6xl sm:text-7xl font-black tabular-nums"
                                style={{
                                    fontFamily: theme.headingFont,
                                    color: '#FFD700',
                                    textShadow: '0 0 24px rgba(255,215,0,0.9), 0 0 60px rgba(255,215,0,0.6)',
                                    letterSpacing: '0.04em',
                                }}
                            >
                                +${winFlash.amount.toFixed(2)}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Center-screen quick chat flash — pops the latest message big
                so eyes scanning the felt catch it instantly, then fades. */}
            <AnimatePresence>
                {chatFlash && (
                    <motion.div
                        key={chatFlash.id}
                        initial={{ opacity: 0, y: -20, scale: 0.85 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.9 }}
                        transition={{ duration: 0.22 }}
                        className="fixed inset-x-0 top-24 z-40 flex justify-center pointer-events-none px-4"
                        data-testid="street-dice-chat-flash"
                    >
                        <div
                            className="rounded-2xl px-4 py-2.5 max-w-md backdrop-blur-md border shadow-2xl"
                            style={{
                                background: 'rgba(0,0,0,0.82)',
                                borderColor: `${theme.accent}88`,
                                boxShadow: `0 0 32px ${theme.glow}66`,
                            }}
                        >
                            <div
                                className="text-[10px] font-black tracking-[0.3em] mb-0.5"
                                style={{ color: theme.accent, fontFamily: theme.headingFont }}
                            >
                                {chatFlash.name.toUpperCase()}
                            </div>
                            <div className="text-lg sm:text-xl text-white leading-tight font-bold">
                                “{chatFlash.text}”
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* House take announcement — briefly overlays the felt whenever
                the corner pays the house (rake or bot winnings). */}
            <AnimatePresence>
                {houseTakeFlash && (
                    <motion.div
                        key={houseTakeFlash.id}
                        initial={{ opacity: 0, y: -8, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-x-0 top-36 z-40 flex justify-center pointer-events-none px-4"
                        data-testid="street-dice-house-take-flash"
                    >
                        <div
                            className="rounded-2xl px-5 py-3 max-w-md backdrop-blur-md border shadow-2xl text-center"
                            style={{
                                background: 'linear-gradient(180deg, rgba(255,215,0,0.18), rgba(0,0,0,0.85))',
                                borderColor: '#FFD70088',
                                boxShadow: '0 0 36px rgba(255,215,0,0.35)',
                            }}
                        >
                            <div className="text-[10px] font-black tracking-[0.35em] text-[#FFD700] mb-0.5">
                                🏛 HOUSE TAKES
                            </div>
                            <div className="text-3xl font-black text-white tabular-nums leading-none">
                                {fmt$(houseTakeFlash.amount)}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">
                                {houseTakeFlash.source === 'bot_winnings'
                                    ? 'from bot winnings'
                                    : houseTakeFlash.source === 'come_out_rake'
                                        ? 'on 2/12 come-out rake'
                                        : 'from the corner'}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Force-fade banner — table-wide play-by-play whenever the room
                books an auto-fade (either shooter-pressed-ROLL or the 20s
                heads-up timeout). Keeps everyone on the same page even
                when the fader was AFK. */}
            <AnimatePresence>
                {forceFadeFlash && (
                    <motion.div
                        key={forceFadeFlash.id}
                        initial={{ opacity: 0, y: -8, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-x-0 top-36 z-40 flex justify-center pointer-events-none px-4"
                        data-testid="street-dice-force-fade-flash"
                    >
                        <div
                            className="rounded-2xl px-5 py-3 max-w-md backdrop-blur-md border shadow-2xl text-center"
                            style={{
                                background: 'linear-gradient(180deg, rgba(255,77,184,0.20), rgba(0,0,0,0.85))',
                                borderColor: '#FF4DB8AA',
                                boxShadow: '0 0 36px rgba(255,77,184,0.35)',
                            }}
                        >
                            <div className="text-[10px] font-black tracking-[0.35em] text-[#FF4DB8] mb-0.5">
                                🔒 AUTO-FADED
                            </div>
                            <div className="text-2xl font-black text-white leading-none">
                                <span className="text-[#FFD700]">{forceFadeFlash.name}</span>{' '}
                                <span className="tabular-nums">{fmt$(forceFadeFlash.amount)}</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">
                                {forceFadeFlash.reason === 'shooter_rolled'
                                    ? 'shooter rolled · come-out only'
                                    : '20s heads-up · come-out only'}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating bot chat feed — right side, above action bar */}
            <div
                className="fixed right-3 bottom-24 z-20 flex flex-col items-end gap-1.5 pointer-events-none max-w-[70vw] sm:max-w-xs"
                data-testid="street-dice-chat-feed"
            >
                <AnimatePresence>
                    {chatMessages.map((c) => (
                        <motion.div
                            key={c.id}
                            initial={{ opacity: 0, x: 40, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-2xl rounded-tr-sm px-3 py-1.5 shadow-lg backdrop-blur-sm border max-w-full"
                            style={{
                                background: c.mentioned_you
                                    ? 'linear-gradient(180deg, rgba(255,215,0,0.22), rgba(0,0,0,0.85))'
                                    : 'rgba(0,0,0,0.75)',
                                borderColor: c.mentioned_you ? '#FFD700cc' : `${theme.accent}55`,
                                boxShadow: c.mentioned_you
                                    ? '0 4px 14px rgba(0,0,0,0.35), 0 0 22px rgba(255,215,0,0.45)'
                                    : `0 4px 14px rgba(0,0,0,0.35), 0 0 12px ${theme.glow}22`,
                            }}
                            data-testid={`street-dice-chat-msg-${c.id}`}
                        >
                            <div
                                className="text-[9px] font-black tracking-widest opacity-70"
                                style={{ color: theme.accent, fontFamily: theme.headingFont }}
                            >
                                {c.name.toUpperCase()}
                            </div>
                            <div className="text-sm text-white leading-tight">
                                {c.text.split(/(@[A-Za-z0-9_\-.]{1,40})/g).map((part, i) => (
                                    part.startsWith('@')
                                        ? <span key={i} className="font-bold" style={{ color: theme.accent }}>{part}</span>
                                        : <span key={i}>{part}</span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Floating emote bursts — trash-talk reactions rise over the pit */}
            <div
                className="fixed inset-x-0 bottom-28 top-24 z-20 pointer-events-none overflow-hidden"
                data-testid="street-dice-reaction-layer"
            >
                <AnimatePresence>
                    {reactions.map((r) => (
                        <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 60, scale: 0.3 }}
                            animate={{ opacity: [0, 1, 1, 0], y: -220, scale: [0.4, 1.25, 1, 0.9] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 2.4, ease: 'easeOut', times: [0, 0.15, 0.7, 1] }}
                            className="absolute bottom-0 flex flex-col items-center"
                            style={{ left: `${r.x}%` }}
                            data-testid={`street-dice-reaction-${r.id}`}
                        >
                            <span className="text-4xl drop-shadow-[0_3px_8px_rgba(0,0,0,0.9)]">{r.emoji}</span>
                            <span
                                className="mt-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-black tracking-wider text-white/90 whitespace-nowrap"
                                style={{ background: 'rgba(0,0,0,0.6)', border: `1px solid ${r.isMe ? '#FFD700aa' : 'rgba(255,255,255,0.12)'}` }}
                            >
                                {r.isMe ? 'You' : r.name}
                            </span>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div
                className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 backdrop-blur-md px-3 pt-2 pb-[env(safe-area-inset-bottom,0.75rem)]"
                style={{ background: 'rgba(0,0,0,0.85)' }}
                data-testid="street-dice-action-bar"
            >
                <div className="max-w-md mx-auto">
                    {/* Typing indicator — shows "Name is typing…" (or up to 2
                        names) above the chat input for ~4s after their last
                        keystroke. Purely cosmetic, no persistence. */}
                    {isSeated && Object.keys(typingUsers).length > 0 && (
                        <div
                            className="text-[11px] text-white/60 italic pb-1 flex items-center gap-1"
                            data-testid="street-dice-typing-indicator"
                        >
                            <span className="inline-flex gap-0.5">
                                <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" style={{ animationDelay: '0ms' }} />
                                <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" style={{ animationDelay: '150ms' }} />
                                <span className="w-1 h-1 rounded-full bg-white/70 animate-pulse" style={{ animationDelay: '300ms' }} />
                            </span>
                            <span>
                                {(() => {
                                    const names = Object.values(typingUsers).map((v) => v.name);
                                    if (names.length === 1) return `${names[0]} is typing…`;
                                    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
                                    return `${names[0]} and ${names.length - 1} others are typing…`;
                                })()}
                            </span>
                        </div>
                    )}
                    {isSeated && (
                        <div data-testid="street-dice-chat-container">
                            {/* Tap-to-send trash-talk emotes — burst over the pit */}
                            <div className="flex gap-1 overflow-x-auto pb-1 mb-1 -mx-1 px-1" data-testid="street-dice-emote-bar">
                                {['🔥', '🎲', '💰', '👑', '💀', '😂', '🙌', '😤', '🐍', '🤑'].map((em) => (
                                    <button
                                        key={em}
                                        type="button"
                                        onClick={() => sendReaction(em)}
                                        className="shrink-0 w-7 h-7 flex items-center justify-center text-sm rounded-full bg-white/5 hover:bg-white/15 active:scale-90 border border-white/10 transition"
                                        style={{ transitionProperty: 'transform, background-color' }}
                                        aria-label={`React ${em}`}
                                        data-testid={`street-dice-emote-${em}`}
                                    >
                                        {em}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-1.5 overflow-x-auto pb-1.5 mb-1 -mx-1 px-1">
                                {[
                                    'Nice roll!', 'Come on 7!', 'Pay that man',
                                    'Fade you!', 'Seven out', 'Hit that point',
                                    'GL', 'Lol',
                                ].map((qc) => (
                                    <button
                                        key={qc}
                                        onClick={() => sendChat(qc)}
                                        className="whitespace-nowrap px-2.5 py-1 text-[11px] rounded-full bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 shrink-0"
                                        data-testid={`street-dice-chat-quick-${qc.replace(/\W+/g, '-').toLowerCase()}`}
                                    >
                                        {qc}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {!isSeated ? (
                        <div className="flex items-center gap-2">
                            <div
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/15 bg-white/5 text-[10px] font-black tracking-widest text-white/70 shrink-0"
                                data-testid="street-dice-spectating-badge"
                            >
                                <Eye className="w-3 h-3" /> SPECTATING
                            </div>
                            <Button
                                onClick={() => {
                                    setBuyIn(String(room.min_buy_in));
                                    setJoinOpen(true);
                                }}
                                className="flex-1 h-12 font-black text-black"
                                style={{ background: theme.accent }}
                                data-testid="street-dice-join-btn"
                            >
                                <LogIn className="w-4 h-4 mr-1" /> TAKE A SEAT
                            </Button>
                        </div>
                    ) : (
                        <ActionBar
                            room={room}
                            isShooter={isShooter}
                            hand={hand}
                            phase={phase}
                            myUserId={user?.id}
                            playableBalance={
                                room.mode === 'real'
                                    ? Number(user?.credits ?? 0) + Number(user?.bonus_credits ?? 0)
                                    : Number(mySeat?.room_balance ?? 0)
                            }
                            announceAmt={announceAmt}
                            setAnnounceAmt={setAnnounceAmt}
                            uncovered={uncovered}
                            busy={busy}
                            onAnnounce={announce}
                            onCover={cover}
                            onRoll={roll}
                            onLeave={leave}
                            theme={theme}
                            lastAnnouncedAmt={lastAnnouncedAmt}
                            onOpenSideBets={() => {
                                setSideBetPostOpen(true);
                                const el = document.getElementById('street-dice-side-bets');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Top-up sheet — tap the WALLET chip to open.
                Two-step, table-side flow:
                  1. Player hits "PAY WITH PAYPAL" → opens the shared PayPal
                     NCP link in a new tab, memo pre-filled with `name · 0009`
                     so the cashier can match it. When they come back to this
                     tab (or close the PayPal one) the dialog stays right here.
                  2. Player uploads the receipt screenshot → same
                     Gemini-OCR + auto-credit path as the main Wallet screen. */}
            <Dialog
                open={topUpOpen}
                onOpenChange={(v) => {
                    setTopUpOpen(v);
                    if (!v) {
                        setTopUpPayPalOpened(false);
                        setTopUpReturnPulse(false);
                    }
                }}
            >
                <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
                    <DialogHeader>
                        <DialogTitle
                            className="text-white"
                            style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                        >
                            Top up at the table
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-white/80 leading-relaxed">
                            Top up your <b className="text-[#FFD700]">BETohalics wallet</b> instantly with PayPal, Venmo, Pay Later or Apple Pay. Credits land in seconds and you drop right back at the table.
                        </p>
                        <Button
                            onClick={() => window.location.assign('/buy-credits')}
                            className="w-full font-black text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-topup-buy-credits"
                        >
                            BUY CREDITS →
                        </Button>
                        <button
                            onClick={() => setTopUpOpen(false)}
                            className="w-full text-white/60 text-sm py-1 hover:text-white/90"
                        >
                            Cancel
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Join dialog */}
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
                <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
                    <DialogHeader>
                        <DialogTitle
                            className="text-white"
                            style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                        >
                            Take a seat
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {room.mode === 'real' ? (
                            // Wallet-tied real-money flow — no buy-in. Just sit.
                            <div className="space-y-2">
                                <p className="text-sm text-white/80 leading-relaxed">
                                    Wagers pull straight from your <b className="text-[#FFD700]">BETohalics wallet</b>. Wins land back in there instantly — no chip stacks to manage.
                                </p>
                                <p className="text-[11px] text-white/50">
                                    Wager range this room: <b className="text-white/80">{fmt$(room.min_wager)} – {fmt$(room.max_wager)}</b> per hand.
                                </p>
                            </div>
                        ) : (
                            // Practice mode — still needs a fun-chip buy-in.
                            <>
                                <p className="text-xs text-white/70">
                                    Buy-in range: <b className="text-white">{fmt$(room.min_buy_in)} – {fmt$(room.max_buy_in)}</b>. Practice mode — no wallet impact.
                                </p>
                                <Label className="text-xs text-white/70">Buy-in</Label>
                                <Input
                                    type="number" min={room.min_buy_in} max={room.max_buy_in}
                                    value={buyIn} onChange={(e) => setBuyIn(e.target.value)}
                                    className="bg-black/40 border-white/10 text-white text-2xl h-14 font-black"
                                    data-testid="street-dice-join-buyin"
                                />
                                <div className="flex flex-wrap gap-2">
                                    {[room.min_buy_in, room.min_buy_in * 2, room.max_buy_in].map((v, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setBuyIn(String(v))}
                                            className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-bold text-white/70"
                                            data-testid={`street-dice-join-quick-${i}`}
                                        >
                                            {fmt$(v)}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="flex gap-2 mt-3">
                            <Button variant="ghost" onClick={() => setJoinOpen(false)} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                            <Button
                                onClick={join}
                                disabled={busy || (room.mode !== 'real' && !buyIn)}
                                className="flex-1 font-black text-black"
                                style={{ background: theme.accent }}
                                data-testid="street-dice-join-submit"
                            >
                                {busy
                                    ? 'Joining…'
                                    : (room.mode === 'real' ? 'TAKE A SEAT' : 'JOIN')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <HouseRulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} theme={theme} />
        </div>
    );
}

// ─── Sub-components ────────────────────────────────────────────────────

const StatChip = ({ label, value, color, theme, testid }) => (
    <div
        className="rounded-lg px-3 py-2 border"
        style={{
            background: 'rgba(0,0,0,0.4)',
            borderColor: `${color}55`,
        }}
        data-testid={testid}
    >
        <p className="text-[9px] tracking-[0.2em] font-black text-white/50">{label}</p>
        <p
            className="text-lg font-black tabular-nums truncate"
            style={{ color, fontFamily: theme.headingFont, letterSpacing: '0.02em' }}
        >
            {value}
        </p>
    </div>
);

const ActionBar = ({
    room, isShooter, hand, phase, playableBalance, announceAmt, setAnnounceAmt,
    uncovered, busy, onAnnounce, onCover, onRoll, onLeave, theme, lastAnnouncedAmt,
    myUserId, onOpenSideBets,
}) => {
    const noHand = !hand || phase === 'waiting' || phase === 'hand_ended';
    // Once seated, if the dice are on the ground (no shooter yet) any human can
    // pick them up by announcing — backend lazily assigns shooter_id on announce.
    const canAnnounce = noHand && (isShooter || !room.shooter_id);
    const canWaitForShooter = noHand && !isShooter;
    const canCover = hand && phase === 'covering' && !isShooter && uncovered > 0;
    const canRoll = hand && isShooter && (phase === 'covering' || phase === 'rolling');

    // Pre-fill the announce input with the last-used amount when idle.
    useEffect(() => {
        if (canAnnounce && !announceAmt && lastAnnouncedAmt) {
            setAnnounceAmt(lastAnnouncedAmt);
        }
    }, [canAnnounce, announceAmt, lastAnnouncedAmt, setAnnounceAmt]);

    // Countdown for the "NEED A FADER" 10s window
    const fadeEndsAt = hand?.fade_window_ends_at ? new Date(hand.fade_window_ends_at).getTime() : null;
    // Heads-up 20s force-fade deadline — backend sets these two fields ONLY
    // when the table has shooter + exactly 1 non-shooter human. If the current
    // user is the target, we render a pulsing red countdown once the initial
    // 10s "need a fader" grace expires. Reported 2026-07-23: without the
    // visual warning, the lone fader had no clue they were about to be
    // force-faded and thought the app just took their money.
    const forceFadeEndsAt = hand?.force_fade_ends_at ? new Date(hand.force_fade_ends_at).getTime() : null;
    const isForceFadeTarget = !!myUserId && hand?.force_fade_target_user_id === myUserId;
    const [now, setNow] = useState(Date.now());
    const [fadeBuild, setFadeBuild] = useState('');
    useEffect(() => {
        if (!fadeEndsAt && !forceFadeEndsAt) return;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [fadeEndsAt, forceFadeEndsAt]);
    const fadeSecondsLeft = fadeEndsAt ? Math.max(0, Math.ceil((fadeEndsAt - now) / 1000)) : 0;
    const forceFadeSecondsLeft = forceFadeEndsAt ? Math.max(0, Math.ceil((forceFadeEndsAt - now) / 1000)) : 0;
    const showForceFadeCountdown = (
        canCover && isForceFadeTarget && forceFadeEndsAt && forceFadeSecondsLeft > 0
    );

    // Cap the fade at whatever the player can actually spend — for real-money
    // rooms this is the live wallet; in practice mode it's the room stack.
    const fadeCap = Math.min(uncovered, Math.max(0, Number(playableBalance) || 0));

    // ── Cash-stack wager builder ──────────────────────────────────────
    // The $1/$5/$10/$25/$100 stacks act like chip denominations: tapping one
    // adds to the active amount. While announcing they build the shooter's
    // wager; while covering they build the fade amount (capped to headroom).
    const DENOMS = [1, 5, 10, 25, 100];
    const buildMode = canAnnounce ? 'announce' : canCover ? 'cover' : null;
    const builtVal = buildMode === 'announce' ? announceAmt : buildMode === 'cover' ? fadeBuild : '';
    const addDenom = (d) => {
        if (buildMode === 'announce') {
            const next = Math.min(Number(room.max_wager), (Number(announceAmt) || 0) + d);
            setAnnounceAmt(String(next));
        } else if (buildMode === 'cover') {
            const next = Math.min(fadeCap, (Number(fadeBuild) || 0) + d);
            setFadeBuild(String(next));
        }
    };
    const clearBuild = () => {
        if (buildMode === 'announce') setAnnounceAmt('');
        else if (buildMode === 'cover') setFadeBuild('');
    };
    const coverAmount = Number(fadeBuild) > 0 ? Math.min(fadeCap, Number(fadeBuild)) : fadeCap;
    const announceReady = !busy && Number(announceAmt) >= Number(room.min_wager);

    // Context-aware primary button (occupies the mockup's "ROLL DICE" slot)
    let primary;
    if (canRoll) {
        primary = {
            label: busy ? 'ROLLING…' : phase === 'covering' ? 'ROLL DICE' : 'ROLL AGAIN',
            onClick: onRoll, disabled: busy, testid: 'street-dice-roll-btn',
        };
    } else if (canCover) {
        primary = {
            label: busy ? 'FADING…' : `FADE ${fmt$(coverAmount)}`,
            onClick: () => { onCover(coverAmount); setFadeBuild(''); },
            disabled: busy || coverAmount <= 0, testid: 'street-dice-cover-btn',
        };
    } else if (canAnnounce) {
        primary = {
            label: busy ? 'ROLLING…' : 'ROLL DICE',
            onClick: () => onAnnounce(), disabled: !announceReady, testid: 'street-dice-announce-btn',
        };
    } else {
        primary = { label: 'WAIT YOUR TURN', onClick: () => {}, disabled: true, testid: 'street-dice-primary-wait' };
    }

    return (
        <div className="flex flex-col gap-2">
            {canWaitForShooter && (
                <div className="text-center text-xs text-white/60 py-1">
                    Waiting for the shooter to announce a wager…
                </div>
            )}

            {/* NEED A FADER prompt — big + pulsing, shows countdown */}
            {canCover && fadeSecondsLeft > 0 && !showForceFadeCountdown && (
                <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-lg border-2 px-3 py-1.5 flex items-center justify-between gap-2"
                    style={{
                        borderColor: theme.accent,
                        background: `${theme.accent}18`,
                        boxShadow: `0 0 18px ${theme.glow}`,
                    }}
                    data-testid="street-dice-need-fader-banner"
                >
                    <motion.span
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        className="text-sm font-black tracking-[0.24em]"
                        style={{ color: theme.accent, fontFamily: theme.headingFont }}
                    >
                        NEED A FADER · {fmt$(uncovered)}
                    </motion.span>
                    <span className="text-xs font-mono text-white/70 tabular-nums">{fadeSecondsLeft}s</span>
                </motion.div>
            )}

            {/* Heads-up FORCED FADE warning — pulsing red for the lone fader */}
            {showForceFadeCountdown && (
                <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{
                        opacity: 1, y: 0,
                        boxShadow: [
                            '0 0 12px rgba(239,68,68,0.4)',
                            '0 0 26px rgba(239,68,68,0.9)',
                            '0 0 12px rgba(239,68,68,0.4)',
                        ],
                    }}
                    transition={{ boxShadow: { duration: 1, repeat: Infinity } }}
                    className="rounded-lg border-2 px-3 py-2 flex items-center justify-between gap-2"
                    style={{ borderColor: '#ef4444', background: 'rgba(239,68,68,0.15)' }}
                    data-testid="street-dice-force-fade-banner"
                >
                    <motion.span
                        animate={{ opacity: [0.55, 1, 0.55] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                        className="text-sm font-black tracking-[0.22em] text-red-300"
                        style={{ fontFamily: theme.headingFont }}
                    >
                        FORCED FADE IN
                    </motion.span>
                    <motion.span
                        key={forceFadeSecondsLeft}
                        initial={{ scale: 1.6, opacity: 0.4 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.25 }}
                        className="text-2xl font-black text-red-400 tabular-nums"
                        data-testid="street-dice-force-fade-seconds"
                    >
                        {forceFadeSecondsLeft}s
                    </motion.span>
                </motion.div>
            )}

            {/* ── Cash-stack denomination row ($1/$5/$10/$25/$100) + SIDE BETS ── */}
            {buildMode && (
                <div data-testid="street-dice-cash-row">
                    <div className="flex items-center justify-between px-0.5 mb-1">
                        <span className="text-[10px] font-black tracking-widest text-white/60">
                            {buildMode === 'announce' ? 'WAGER' : 'YOUR FADE'}
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-black tabular-nums text-[#FFD700]" data-testid="street-dice-built-amount">
                                {fmt$(builtVal || 0)}
                            </span>
                            <input
                                type="number" min={room.min_wager} max={buildMode === 'cover' ? fadeCap : room.max_wager}
                                value={builtVal}
                                onChange={(e) => buildMode === 'announce' ? setAnnounceAmt(e.target.value) : setFadeBuild(e.target.value)}
                                placeholder={`${room.min_wager}–${buildMode === 'cover' ? Math.floor(fadeCap) : room.max_wager}`}
                                className="w-16 bg-black/50 border border-[#FFD700]/30 rounded-md px-2 py-1 text-xs text-white text-right focus:outline-none focus:border-[#FFD700]/70"
                                data-testid="street-dice-announce-input"
                            />
                        </div>
                    </div>
                    <div className="flex items-end gap-2">
                        <div className="flex-1 flex items-end justify-between gap-1">
                            {DENOMS.map((d) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => addDenom(d)}
                                    disabled={busy}
                                    className="group relative flex flex-col items-center gap-1 px-0.5 pt-2 active:scale-95 transition disabled:opacity-40"
                                    data-testid={`street-dice-denom-${d}`}
                                    title={`Add $${d}`}
                                >
                                    <span className="relative block w-11 h-8 group-hover:-translate-y-1 transition-transform">
                                        {[0, 5, 10, 15].map((b, idx) => (
                                            <span key={b} className="absolute rounded-[3px] border flex items-center justify-center"
                                                style={{
                                                    left: 2 + idx * 0.5, right: 2 + idx * 0.5, bottom: b, height: 8,
                                                    background: `linear-gradient(to bottom, hsl(142 42% ${44 + idx * 4}%), hsl(142 46% ${26 + idx * 3}%))`,
                                                    borderColor: '#123a21',
                                                    boxShadow: idx === 3 ? 'inset 0 1px 2px rgba(255,255,255,0.3)' : '0 1px 2px rgba(0,0,0,0.5)',
                                                }}>
                                                {idx === 3 && <span className="text-[7px] font-black text-[#0c3b1d]">$</span>}
                                            </span>
                                        ))}
                                        <span className="absolute left-3 right-3 bottom-[14px] h-1 rounded-sm" style={{ background: 'linear-gradient(90deg,#FFF2B0,#FFD700,#C98A1A)', boxShadow: '0 0 5px rgba(255,215,0,0.8)' }} />
                                    </span>
                                    <span className="text-[13px] font-black text-[#FFD700] drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)] group-hover:drop-shadow-[0_0_7px_rgba(255,215,0,0.8)]">${d}</span>
                                </button>
                            ))}
                        </div>
                        {isShooter && (
                            <button
                                type="button"
                                onClick={() => onOpenSideBets?.()}
                                className="shrink-0 flex flex-col items-center gap-0.5 rounded-xl border px-3 py-2 text-[#FFD700]"
                                style={{ borderColor: 'rgba(255,215,0,0.6)', background: 'linear-gradient(135deg,rgba(255,215,0,0.14),rgba(0,0,0,0.5))', boxShadow: '0 0 12px rgba(255,215,0,0.25)' }}
                                data-testid="street-dice-side-bets-btn"
                            >
                                <Sparkles className="w-4 h-4" />
                                <span className="text-[8px] font-black tracking-widest">SIDE BETS</span>
                                <span className="text-[6px] font-semibold text-white/45 -mt-0.5">offer action</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Non-shooter, mid-hand status */}
            {hand && !isShooter && !canCover && phase !== 'hand_ended' && (
                <div className="text-center text-xs text-white/60 py-1">
                    {phase === 'covering'
                        ? 'Fully covered — shooter is on deck.'
                        : `Shooter rolling on ${room.point ? `point ${room.point}` : 'come-out'}…`}
                </div>
            )}

            {/* ── CLEAR · ROLL DICE · REPEAT — primary bottom controls ── */}
            <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-stretch">
                <button
                    type="button"
                    onClick={clearBuild}
                    disabled={!buildMode || busy}
                    className="rounded-xl border border-white/20 bg-white/5 px-4 flex flex-col items-center justify-center text-white/80 hover:bg-white/10 active:scale-95 transition disabled:opacity-30"
                    data-testid="street-dice-clear-btn"
                    title="Clear wager"
                >
                    <Eraser className="w-5 h-5" />
                    <span className="text-[9px] font-black tracking-widest mt-0.5">CLEAR</span>
                </button>

                <motion.button
                    type="button"
                    onClick={primary.onClick}
                    disabled={primary.disabled}
                    animate={!primary.disabled && (canRoll || canAnnounce)
                        ? { boxShadow: ['0 0 14px rgba(255,215,0,0.4)', '0 0 28px rgba(255,215,0,0.8)', '0 0 14px rgba(255,215,0,0.4)'] }
                        : {}}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="rounded-xl h-16 flex flex-col items-center justify-center font-black text-black tracking-wide disabled:opacity-40 disabled:grayscale active:scale-[0.98] transition"
                    style={{ background: 'linear-gradient(180deg,#FFE680 0%,#FFD700 50%,#D99A14 100%)', fontFamily: theme.headingFont }}
                    data-testid={primary.testid}
                >
                    <span className="flex items-center gap-2 text-xl leading-none"><Dice5 className="w-6 h-6" />{primary.label}</span>
                    {buildMode === 'announce' && Number(announceAmt) > 0 && (
                        <span className="text-[10px] font-bold tracking-widest text-black/70 mt-0.5">WAGER {fmt$(announceAmt)}</span>
                    )}
                </motion.button>

                <button
                    type="button"
                    onClick={() => {
                        if (canAnnounce && lastAnnouncedAmt) { onAnnounce(lastAnnouncedAmt); }
                    }}
                    disabled={busy || !lastAnnouncedAmt || !canAnnounce}
                    className="rounded-xl border border-white/20 bg-white/5 px-4 flex flex-col items-center justify-center text-white/80 hover:bg-white/10 active:scale-95 transition disabled:opacity-30"
                    data-testid="street-dice-repeat-btn"
                    title={lastAnnouncedAmt ? `Repeat last wager ($${lastAnnouncedAmt})` : 'No previous wager'}
                >
                    <RotateCcw className="w-5 h-5" />
                    <span className="text-[9px] font-black tracking-widest mt-0.5">REPEAT</span>
                </button>
            </div>

            {/* Leave button */}
            <button
                onClick={onLeave}
                disabled={busy}
                className="self-end text-[11px] text-white/50 hover:text-white/90 flex items-center gap-1"
                data-testid="street-dice-leave-btn"
            >
                <DoorOpen className="w-3 h-3" /> Cash out & leave
            </button>
        </div>
    );
};


// ─── Side Bets Panel ──────────────────────────────────────────────────

const SB_LABEL = {
    point_hit: { title: 'Point Hit', tag: 'HIT BEFORE 7-OUT', icon: Target },
    seven_first: { title: 'Seven First', tag: '7 BEFORE THE POINT', icon: Flame },
    six_eight: { title: '6 / 8', tag: '6 OR 8 BEFORE 7', icon: Target },
    ten_four: { title: '10 / 4', tag: '10 OR 4 BEFORE 7', icon: Target },
    five_nine: { title: '5 / 9', tag: '5 OR 9 BEFORE 7', icon: Target },
    // Legacy — kept only so old settled hands don't render as "undefined"
    any_craps: { title: 'Any Craps', tag: 'NEXT ROLL 2/3/12', icon: Flame },
};

const SB_RESOLUTION_LABEL = {
    any_craps_hit: 'CRAPS HIT',
    no_craps: 'NO CRAPS',
    point_hit: 'POINT MADE',
    seven_out: 'SEVENED OUT',
    seven_first: 'SEVEN FIRST',
    seven_before: 'SEVENED OUT',
    place_4: '4 HIT',
    place_5: '5 HIT',
    place_6: '6 HIT',
    place_8: '8 HIT',
    place_9: '9 HIT',
    place_10: '10 HIT',
    voided_unmatched: 'VOIDED — UNMATCHED',
    voided_hand_ended: 'VOIDED — HAND ENDED',
    cancelled_by_poster: 'CANCELLED',
};

const SideBetsPanel = ({ room, user, isSeated, theme, busy, onPost, onAccept, onCancel, postOpen, setPostOpen }) => {
    const hand = room?.hand || {};
    const sideBets = hand.side_bets || [];

    const canPost = isSeated && !hand.settled;
    // Both point-based side bets require a point to be established
    const canPostPointHit = canPost && !!room.point && hand.phase === 'rolling';
    const canPostSevenFirst = canPost && !!room.point && hand.phase === 'rolling';
    // Place-win pairs — available anytime a hand is rolling (come-out or point).
    const canPostPlaceBet = canPost && (hand.phase === 'covering' || hand.phase === 'rolling');
    const anyPostable = canPostPointHit || canPostSevenFirst || canPostPlaceBet;

    const openActive = sideBets.filter((sb) => sb.status === 'open' || sb.status === 'matched');
    const settled = sideBets.filter((sb) => sb.status === 'settled' || sb.status === 'voided');

    if (openActive.length === 0 && settled.length === 0 && !canPost) {
        return null; // Nothing to show and no way to post — hide entirely
    }

    return (
        <div
            className="mt-4 rounded-2xl border p-3"
            style={{
                borderColor: `${theme.accent}44`,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)',
            }}
            data-testid="street-dice-side-bets-panel"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4" style={{ color: theme.accent }} />
                    <p
                        className="text-xs tracking-[0.28em] font-black text-white/80"
                        style={{ fontFamily: theme.headingFont }}
                    >
                        SIDE BETS ({openActive.length})
                    </p>
                </div>
                {canPost && (
                    <button
                        onClick={() => setPostOpen(true)}
                        disabled={busy || !anyPostable}
                        className="inline-flex items-center gap-1 text-[11px] font-black tracking-widest px-2.5 py-1 rounded-full text-black disabled:opacity-40"
                        style={{ background: theme.accent }}
                        data-testid="street-dice-post-side-bet-btn"
                    >
                        <Plus className="w-3.5 h-3.5" /> CHALLENGE
                    </button>
                )}
            </div>

            {openActive.length === 0 && settled.length === 0 && (
                <p className="text-[11px] text-white/50 italic py-2">
                    No side action yet — call your shot.
                </p>
            )}

            <div className="space-y-2">
                {openActive.map((sb) => (
                    <SideBetRow
                        key={sb.id}
                        sb={sb}
                        user={user}
                        isSeated={isSeated}
                        theme={theme}
                        busy={busy}
                        onAccept={onAccept}
                        onCancel={onCancel}
                    />
                ))}
            </div>

            {settled.length > 0 && (
                <details className="mt-2">
                    <summary className="text-[10px] tracking-widest uppercase text-white/40 cursor-pointer hover:text-white/70">
                        Settled ({settled.length})
                    </summary>
                    <div className="mt-2 space-y-1">
                        {settled.slice(-5).reverse().map((sb) => (
                            <SettledSideBetRow key={sb.id} sb={sb} theme={theme} />
                        ))}
                    </div>
                </details>
            )}

            <PostSideBetDialog
                open={postOpen}
                onClose={() => setPostOpen(false)}
                theme={theme}
                canPostPointHit={canPostPointHit}
                canPostSevenFirst={canPostSevenFirst}
                canPostPlaceBet={canPostPlaceBet}
                onSubmit={(type, stake) => {
                    onPost(type, stake);
                    setPostOpen(false);
                }}
                busy={busy}
                point={room.point}
            />
        </div>
    );
};

const SideBetRow = ({ sb, user, isSeated, theme, busy, onAccept, onCancel }) => {
    const info = SB_LABEL[sb.type] || { title: sb.type, tag: '', icon: Flame };
    const Icon = info.icon;
    const isMine = sb.poster_id === user?.id;
    const iAccepted = sb.acceptor_id === user?.id;
    const stake = parseFloat(sb.poster_stake || 0);
    const matched = sb.status === 'matched';

    return (
        <div
            className="rounded-lg border px-2.5 py-2 flex items-center gap-2"
            style={{
                borderColor: matched ? `${theme.accent}77` : 'rgba(255,255,255,0.12)',
                background: matched ? `${theme.accent}12` : 'rgba(255,255,255,0.04)',
            }}
            data-testid={`street-dice-side-bet-${sb.id}`}
        >
            <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${theme.accent}22`, color: theme.accent }}
            >
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <p className="text-sm font-black text-white truncate">{info.title}</p>
                    <span className="text-[9px] font-mono text-white/40 tracking-widest hidden sm:inline">
                        {info.tag}
                    </span>
                </div>
                <p className="text-[10px] text-white/60 truncate">
                    {sb.poster_name} <span className="text-white/40">vs.</span> {matched ? (
                        <span style={{ color: theme.accentSoft }}>{sb.acceptor_name}</span>
                    ) : (
                        <span className="italic text-white/40">looking for a taker</span>
                    )}
                </p>
            </div>
            <div className="text-right min-w-[60px]">
                <p className="text-sm font-black tabular-nums" style={{ color: theme.potColor }}>
                    ${stake.toFixed(0)}
                </p>
                <p className="text-[9px] text-white/40 uppercase tracking-widest">
                    {matched ? 'live' : 'open'}
                </p>
            </div>
            {sb.status === 'open' && !isMine && isSeated && (
                <button
                    onClick={() => onAccept(sb)}
                    disabled={busy}
                    className="text-sm font-black tracking-widest px-4 py-2.5 rounded-lg text-black shrink-0 min-w-[90px] whitespace-nowrap"
                    style={{ background: theme.accent, boxShadow: `0 0 12px ${theme.glow}` }}
                    data-testid={`street-dice-side-bet-accept-${sb.id}`}
                    title="Fade this challenge"
                >
                    FADE ${stake.toFixed(0)}
                </button>
            )}
            {sb.status === 'open' && isMine && (
                <button
                    onClick={() => onCancel(sb)}
                    disabled={busy}
                    className="text-white/50 hover:text-red-400 shrink-0"
                    data-testid={`street-dice-side-bet-cancel-${sb.id}`}
                    title="Cancel challenge"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
            {iAccepted && (
                <div className="text-[9px] font-black text-[#22c55e] flex items-center gap-0.5 shrink-0">
                    <Check className="w-3 h-3" /> FADED
                </div>
            )}
        </div>
    );
};

const SettledSideBetRow = ({ sb, theme }) => {
    const info = SB_LABEL[sb.type] || { title: sb.type, icon: Flame };
    const Icon = info.icon;
    const won = sb.status === 'settled' && !!sb.winner_id;
    const label = SB_RESOLUTION_LABEL[sb.resolution] || (sb.resolution || 'settled').toUpperCase();
    const pot = parseFloat(sb.poster_stake || 0) + parseFloat(sb.acceptor_stake || 0);
    return (
        <div className="flex items-center gap-2 text-[11px] rounded-md px-2 py-1 bg-white/5">
            <Icon className="w-3 h-3 text-white/40" />
            <span className="font-mono text-white/70 truncate">{info.title}</span>
            <span
                className="ml-auto text-[9px] font-black tracking-widest"
                style={{ color: won ? theme.accent : '#a1a1aa' }}
            >
                {label}
            </span>
            {won && pot > 0 && (
                <span className="text-[10px] font-mono tabular-nums text-white/70">
                    ${pot.toFixed(0)}
                </span>
            )}
        </div>
    );
};

const PostSideBetDialog = ({
    open, onClose, theme, canPostPointHit, canPostSevenFirst, canPostPlaceBet,
    onSubmit, busy, point,
}) => {
    const [type, setType] = useState('point_hit');
    const [stake, setStake] = useState('5');

    useEffect(() => {
        if (!open) return;
        // Default to whichever type is currently available (point_hit if both)
        const first = canPostPointHit ? 'point_hit'
            : canPostSevenFirst ? 'seven_first'
                : canPostPlaceBet ? 'six_eight'
                    : 'point_hit';
        setType(first);
        setStake('5');
    }, [open, canPostPointHit, canPostSevenFirst, canPostPlaceBet]);

    // Small helper for rendering a challenge-type button (memoized outside
    // the parent to avoid remounting the subtree on every render).
    const renderTypeButton = (value, enabled, title, subtitle, testid) => (
        <button
            type="button"
            onClick={() => enabled && setType(value)}
            disabled={!enabled}
            className={`rounded-lg border py-2 px-2 text-left disabled:opacity-40 ${type === value ? 'text-black' : 'text-white'}`}
            style={{
                background: type === value ? theme.accent : 'transparent',
                borderColor: type === value ? theme.accent : 'rgba(255,255,255,0.15)',
            }}
            data-testid={testid}
        >
            <div className="text-xs font-black tracking-widest">{title}</div>
            <div className="text-[10px] opacity-80 mt-0.5">{subtitle}</div>
        </button>
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0B0B0B] text-white border" style={{ borderColor: `${theme.accent}55` }}>
                <DialogHeader>
                    <DialogTitle
                        className="text-white"
                        style={{ fontFamily: theme.headingFont, letterSpacing: theme.headingLetter }}
                    >
                        Post a challenge
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] tracking-widest text-white/60">POINT BETS</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                            {renderTypeButton('point_hit', canPostPointHit, 'POINT HIT', point ? `Shooter hits ${point} before 7` : 'Need a point set', 'street-dice-post-type-point-hit')}
                            {renderTypeButton('seven_first', canPostSevenFirst, 'SEVEN FIRST', point ? `Shooter rolls 7 before ${point}` : 'Need a point set', 'street-dice-post-type-seven-first')}
                        </div>
                    </div>

                    <div>
                        <Label className="text-[10px] tracking-widest text-white/60">PLACE-WIN PAIRS · BEFORE A 7</Label>
                        <div className="grid grid-cols-3 gap-2 mt-1.5">
                            {renderTypeButton('six_eight', canPostPlaceBet, '6 / 8', 'Hit 6 or 8 first', 'street-dice-post-type-six-eight')}
                            {renderTypeButton('ten_four', canPostPlaceBet, '10 / 4', 'Hit 10 or 4 first', 'street-dice-post-type-ten-four')}
                            {renderTypeButton('five_nine', canPostPlaceBet, '5 / 9', 'Hit 5 or 9 first', 'street-dice-post-type-five-nine')}
                        </div>
                    </div>

                    <div>
                        <Label className="text-[10px] tracking-widest text-white/60">YOUR STAKE</Label>
                        <Input
                            type="number" min="1" value={stake}
                            onChange={(e) => setStake(e.target.value)}
                            className="bg-black/40 border-white/10 text-white text-xl h-12 font-black mt-1"
                            data-testid="street-dice-post-side-bet-stake"
                        />
                        <div className="flex gap-1.5 mt-2">
                            {[5, 10, 25, 50].map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setStake(String(v))}
                                    className="px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 text-[11px] font-bold text-white/70"
                                >
                                    ${v}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-black/40 p-2 text-[11px] text-white/70 leading-snug">
                        Even money — whoever accepts matches your stake. Winner takes the pot.
                        If nobody fades your challenge before it&apos;d resolve, you get a full refund.
                    </div>

                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={onClose} className="flex-1 text-white/70" disabled={busy}>Cancel</Button>
                        <Button
                            onClick={() => onSubmit(type, stake)}
                            disabled={busy || (!canPostPointHit && !canPostSevenFirst && !canPostPlaceBet)}
                            className="flex-1 font-black text-black"
                            style={{ background: theme.accent }}
                            data-testid="street-dice-post-side-bet-submit"
                        >
                            {busy ? 'Posting…' : 'POST CHALLENGE'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
